import { CornerDownRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ReasonSegmentSaveState } from '../store';
import type { ReasonCode, ReasonSegment } from '../types';
import { isSegmentComplete } from '../types';

interface SegmentNavigatorProps {
  segments: ReasonSegment[];
  currentIndex: number;
  selectableCodes?: ReadonlySet<ReasonCode>;
  useCommittedFields: boolean;
  saveStates?: Record<number, ReasonSegmentSaveState>;
  onNavigate: (index: number) => void | Promise<void>;
}

function formatSegmentTitle(segment: ReasonSegment, index: number, complete: boolean): string {
  const duration = Math.max(0, segment.end_time_s - segment.start_time_s);
  return `Segment ${index + 1} · ${segment.start_time_s.toFixed(2)}s–${segment.end_time_s.toFixed(2)}s · ${duration.toFixed(2)}s · ${complete ? 'Complete' : 'Incomplete'}`;
}

function saveStateLabel(state: ReasonSegmentSaveState | undefined): string {
  if (state === 'queued') return 'Queued';
  if (state === 'saving') return 'Saving';
  if (state === 'failed') return 'Save failed';
  return 'Saved';
}

export function SegmentNavigator({
  segments,
  currentIndex,
  selectableCodes,
  useCommittedFields,
  saveStates = {},
  onNavigate,
}: SegmentNavigatorProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const [goToValue, setGoToValue] = useState(String(currentIndex + 1));
  const [goToError, setGoToError] = useState(false);

  useEffect(() => {
    setGoToValue(String(currentIndex + 1));
    setGoToError(false);
    const current = railRef.current?.querySelector<HTMLElement>(
      `[data-segment-position="${currentIndex}"]`
    );
    const rail = railRef.current;
    if (rail && current) {
      const centeredLeft = current.offsetLeft + current.offsetWidth / 2 - rail.clientWidth / 2;
      rail.scrollLeft = Math.max(0, Math.min(centeredLeft, rail.scrollWidth - rail.clientWidth));
    }
  }, [currentIndex]);

  const submitGoTo = () => {
    const value = Number(goToValue);
    if (!Number.isInteger(value) || value < 1 || value > segments.length) {
      setGoToError(true);
      return;
    }
    setGoToError(false);
    void onNavigate(value - 1);
  };

  const wideRail = segments.length > 40;
  const episodeEnd = Math.max(1, ...segments.map((segment) => segment.end_time_s));

  return (
    <section
      className="mt-4 rounded-lg border border-brand-border bg-brand-surface p-4"
      aria-label="Segment navigation"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-brand-text-primary">Segment progress</h2>
          <p className="mt-1 text-xs text-brand-text-secondary">
            Select a segment to review or finish.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="block text-xs text-brand-text-secondary" htmlFor="go-to-segment">
            Go to Segment
            <input
              id="go-to-segment"
              type="number"
              min={1}
              max={segments.length}
              step={1}
              value={goToValue}
              aria-invalid={goToError}
              onChange={(event) => {
                setGoToValue(event.target.value);
                setGoToError(false);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submitGoTo();
              }}
              className="mt-1 block h-9 w-24 rounded-md border border-brand-border bg-brand-dark px-3 font-mono text-sm text-brand-text-primary outline-none focus:border-brand-orange aria-invalid:border-red-400"
            />
          </label>
          <button
            type="button"
            onClick={submitGoTo}
            aria-label="Go to Segment"
            title="Go to Segment"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-border text-brand-text-primary hover:border-brand-orange hover:text-brand-orange"
          >
            <CornerDownRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {goToError ? (
        <p className="mt-2 text-right text-xs text-red-300" role="alert">
          Enter a Segment from 1 to {segments.length}.
        </p>
      ) : null}

      <div className="mt-4">
        <div className="mb-1 flex items-center justify-between text-[11px] text-brand-text-secondary">
          <span>Episode Segment Map</span>
          <span className="font-mono">0.00s – {episodeEnd.toFixed(2)}s</span>
        </div>
        <div className="relative h-10 overflow-hidden rounded-md border border-brand-border bg-brand-dark">
          {segments.map((segment, index) => {
            const complete = isSegmentComplete(segment, selectableCodes, useCommittedFields);
            const current = index === currentIndex;
            const saveState = saveStates[segment.segment_index];
            const title = `${formatSegmentTitle(segment, index, complete)} · ${saveStateLabel(saveState)}`;
            const left = Math.max(0, Math.min(100, (segment.start_time_s / episodeEnd) * 100));
            const width = Math.max(
              0.6,
              Math.min(100 - left, ((segment.end_time_s - segment.start_time_s) / episodeEnd) * 100)
            );
            return (
              <button
                key={`map-${segment.segment_index}`}
                type="button"
                onClick={() => void onNavigate(index)}
                aria-label={title}
                aria-current={current ? 'step' : undefined}
                title={title}
                className={[
                  'absolute top-1 bottom-1 min-w-1 rounded-sm border focus:outline-none focus:ring-2 focus:ring-brand-orange',
                  complete
                    ? 'border-emerald-300/70 bg-emerald-500/70'
                    : 'border-brand-text-secondary/60 bg-brand-surface',
                  current ? 'z-10 ring-2 ring-brand-orange' : '',
                  saveState === 'failed' ? 'border-red-300 bg-red-500/70' : '',
                  saveState === 'saving' || saveState === 'queued' ? 'animate-pulse' : '',
                ].join(' ')}
                style={{ left: `${left}%`, width: `${width}%` }}
              />
            );
          })}
        </div>
      </div>

      <div ref={railRef} className="mt-4 max-w-full overflow-x-auto pb-1">
        <div
          className="grid gap-1"
          style={{
            gridTemplateColumns: `repeat(${segments.length}, minmax(24px, 1fr))`,
            minWidth: wideRail ? `${segments.length * 28}px` : '100%',
          }}
        >
          {segments.map((segment, index) => {
            const complete = isSegmentComplete(segment, selectableCodes, useCommittedFields);
            const current = index === currentIndex;
            const saveState = saveStates[segment.segment_index];
            const title = `${formatSegmentTitle(segment, index, complete)} · ${saveStateLabel(saveState)}`;
            return (
              <button
                key={segment.segment_index}
                type="button"
                data-segment-position={index}
                onClick={() => void onNavigate(index)}
                aria-label={title}
                aria-current={current ? 'step' : undefined}
                title={title}
                className={[
                  'h-7 min-w-6 rounded-sm border transition-colors focus:outline-none focus:ring-2 focus:ring-brand-orange',
                  complete
                    ? 'border-emerald-400/60 bg-emerald-500/70'
                    : 'border-brand-border bg-brand-dark',
                  current ? 'ring-2 ring-brand-orange ring-offset-2 ring-offset-brand-surface' : '',
                  saveState === 'failed' ? 'border-red-300 bg-red-500/70' : '',
                  saveState === 'saving' || saveState === 'queued' ? 'animate-pulse' : '',
                ].join(' ')}
              />
            );
          })}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-4 text-xs text-brand-text-secondary">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500/70" /> Complete
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm border border-brand-border bg-brand-dark" />{' '}
          Incomplete
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm ring-2 ring-brand-orange" /> Current
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 animate-pulse rounded-sm border border-amber-300 bg-amber-400/50" />{' '}
          Saving
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm border border-red-300 bg-red-500/70" /> Save
          failed
        </span>
      </div>
    </section>
  );
}
